/**
 * Chat history for Kobikuul (web). Fetches paginated history and supports load-more.
 */
import { useCallback, useState } from "react";
import DataProvider from "../services";
import type { ChatMessage } from "../types/chat";
import { getAxiosErrorUserMessage } from "../utils/apiErrorMessage";
import { chatHistoryItemsToMessages } from "../utils/chatHistoryUtils";

const PAGE_SIZE = 20;

export interface UseChatHistoryResult {
  messages: ChatMessage[];
  setMessages: (updater: (prev: ChatMessage[]) => ChatMessage[]) => void;
  refresh: (background?: boolean) => Promise<void>;
  isLoading: boolean;
  historyPage: number;
  historyTotalPages: number;
  isLoadingMore: boolean;
  loadMore: () => Promise<void>;
  error: string | null;
}

export function useChatHistory(chatId: string): UseChatHistoryResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (pageNumber: number, background: boolean) => {
      if (chatId === "default") {
        if (pageNumber === 1) setMessages([]);
        setHistoryPage(1);
        setHistoryTotalPages(1);
        setError(null);
        if (!background) setIsLoading(false);
        return;
      }
      if (!background) setIsLoading(true);
      setError(null);
      try {
        const { data } = await DataProvider.getKobikuulChatHistory(
          pageNumber,
          PAGE_SIZE,
        );
        const totalPages = data?.totalPages ?? 1;
        setHistoryTotalPages(totalPages);
        setHistoryPage(pageNumber);

        if (!data?.chatHistories?.length) {
          if (pageNumber === 1) setMessages([]);
          return;
        }

        const items = [...(data.chatHistories ?? [])].reverse();
        const nextMessages = chatHistoryItemsToMessages(items);
        setMessages(nextMessages);
      } catch (err: unknown) {
        setError(getAxiosErrorUserMessage(err));
        if (pageNumber === 1) setMessages([]);
      } finally {
        if (!background) setIsLoading(false);
      }
    },
    [chatId],
  );

  const refresh = useCallback(
    async (background = false) => {
      await fetchPage(1, background);
    },
    [fetchPage],
  );

  const loadMore = useCallback(async () => {
    if (
      chatId === "default" ||
      historyPage >= historyTotalPages ||
      isLoadingMore
    )
      return;
    const nextPage = historyPage + 1;
    setIsLoadingMore(true);
    try {
      const { data } = await DataProvider.getKobikuulChatHistory(
        nextPage,
        PAGE_SIZE,
      );
      if (!data?.chatHistories?.length) {
        setHistoryPage(nextPage);
        return;
      }
      const olderItems = [...(data.chatHistories ?? [])].reverse();
      const olderMessages = chatHistoryItemsToMessages(olderItems);
      setHistoryPage(nextPage);
      setMessages((prev) => [...olderMessages, ...prev]);
    } catch {
      // keep current state
    } finally {
      setIsLoadingMore(false);
    }
  }, [chatId, historyPage, historyTotalPages, isLoadingMore]);

  return {
    messages,
    setMessages,
    refresh,
    isLoading,
    historyPage,
    historyTotalPages,
    isLoadingMore,
    loadMore,
    error,
  };
}
